import { Box, LinearProgress, Typography, useTheme } from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";

import PMBaseCard, { PMBaseCardProps } from "../pm-base-card/pm-base-card";

export interface PMCategoryProgressDatum {
  /** Main label for this row */
  label: string;
  /** Sub-label for this row */
  subLabel?: string;
  /** Numeric value to display and to fill bar */
  value: number;
  /** Optional total for showing completed/total (percentage%) */
  total?: number;
  /** Optional color for the progress bar */
  color?: string;
}

export interface PMCategoryProgressCardProps extends Omit<
  PMBaseCardProps,
  "children"
> {
  /** Array of up to three category items */
  data: PMCategoryProgressDatum[];
  /** Max value for 100% fill; defaults to largest value in data */
  maxValue?: number;
  /** Card header props (icon, title, etc.)... */
}

/**
 * Category progress card with up to three horizontal progress bars.
 *
 * @component
 * @param {PMCategoryProgressDatum[]} data  Array of up to three category items to display (each with `label`, `subLabel`, `value`, and optional `color`)
 * @param {number} [maxValue]              Maximum value for 100% fill (defaults to highest value in data)
 * @param {React.ReactNode|string} [icon]  Icon for the card header (default "Star")
 * @param {string|React.ReactNode} [title] Card title (default "Title")
 * @param {boolean} [showInfoIcon]         Whether to show the info ("i") icon in the header (default: true)
 * @param {boolean} [showMenuIcon]         Whether to show the menu ("⋮") icon in the header (default: true)
 * @param {number|string} [fixedWidth]     Fixed width of the card (default "100%")
 * @param {number|string} [fixedHeight]    Fixed height of the card (default "auto")
 * @param {...PMBaseCardProps} rest        Any additional PMBaseCard props (e.g., sx, event handlers)
 */
export const PMCategoryProgressCard: PimoReactComponent<
  PMCategoryProgressCardProps
> = ({
  data,
  maxValue,
  icon = "BarChart",
  title = "Title",
  showInfoIcon = true,
  showMenuIcon = true,
  fixedWidth = "100%",
  fixedHeight = "auto",
  ...rest
}) => {
  const theme = useTheme();
  const visibleData = data;
  const ceiling =
    maxValue !== undefined
      ? maxValue
      : Math.max(...visibleData.map((d) => d.value), 0);

  const getDisplayText = (item: PMCategoryProgressDatum) =>
    item.total != null
      ? `${item.value}/${item.total} (${item.total > 0 ? Math.round((item.value / item.total) * 100) : 0}%)`
      : `${item.value}`;

  const maxValueTextLength = Math.max(
    ...visibleData.map((item) => getDisplayText(item).length),
    0
  );

  return (
    <PMBaseCard
      icon={icon}
      title={title}
      showInfoIcon={showInfoIcon}
      showMenuIcon={showMenuIcon}
      fixedWidth={fixedWidth}
      fixedHeight={fixedHeight}
      {...rest}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        {visibleData.map((item, idx) => {
          const percent =
            item.total != null
              ? item.total > 0
                ? (item.value / item.total) * 100
                : 0
              : ceiling > 0
                ? (item.value / ceiling) * 100
                : 0;
          return (
            <Box key={idx} sx={{ mb: idx < visibleData.length - 1 ? 0.25 : 0 }}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.primary }}
                >
                  {item.label}
                </Typography>
                {item.subLabel && (
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary, mb: 1 }}
                  >
                    {item.subLabel}
                  </Typography>
                )}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mt: 0.5,
                }}
              >
                <Box sx={{ flexGrow: 1, mr: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={percent}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      background: theme.palette.grey[200],
                      "& .MuiLinearProgress-bar": {
                        backgroundColor:
                          item.color || theme.palette.primary.main,
                      },
                    }}
                  />
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    width: `${maxValueTextLength}ch`,
                    flexShrink: 0,
                    textAlign: "right",
                    whiteSpace: "nowrap",
                  }}
                >
                  {getDisplayText(item)}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </PMBaseCard>
  );
};

export default PMCategoryProgressCard;
